import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { videoType, vimeoCoverImageUrl, youtubeCoverImageUrl } from '@core/helpers/video';
import {
  AccountService,
  ContentService,
  ContextService,
  FilesService,
  PlatformService,
  RequestService,
  ScreenService,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { TranslateService } from '@ngx-translate/core';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import {
  Content,
  ContentTypeEnum,
  ImageSizeEnum,
  ModerationStatusEnum,
  Router as ContentRouter,
  SubjectCategoryEnum,
  User,
  Video as VideoContent,
} from 'lingo2-models';
import { OnUiCover } from 'onclass-ui';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-content-card-mini-readonly',
  templateUrl: './content-card-mini-readonly.component.html',
  styleUrls: ['./content-card-mini-readonly.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentCardMiniReadonlyComponent extends ChangableComponent implements OnInit {
  @Input() active: boolean;
  @Input() invalid: boolean;
  @Input() linkEnable = false;
  @Input() isLessonSelected: boolean;

  public author: User;
  public authorName: string;
  public authorFullName: string;
  public subject: string;
  public level: string;
  public contentType: string;
  public contentTypeTitle: string;
  public coverImageUrl: string;
  public fullTitle: string;
  public cutLength = 12;
  public subjectCategoryEnum = SubjectCategoryEnum;
  public debug$ = this.contextService.debug$;
  public cover: OnUiCover;

  private _item: Content | Partial<Content>;

  constructor(
    public errorNotificationService: ErrorNotificationService,
    private contextService: ContextService,
    private contentService: ContentService,
    private screenService: ScreenService,
    private translate: TranslateService,
    protected request: RequestService,
    protected accountService: AccountService,
    protected router: Router,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
  ) {
    super(cdr, platform);
  }

  @Input() set item(content: Content | Partial<Content>) {
    this._item = content;
    this.prepareContentCard();
  }

  get item(): Content | Partial<Content> {
    return this._item;
  }

  ngOnInit() {
    this.screenService.width$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (width) => {
        this.cutLength = width > 760 ? 20 : 15;
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        this.cutLength = window.innerWidth > 760 ? 20 : 15;
      },
    });
  }

  public get isVideo(): boolean {
    return this.item.type === ContentTypeEnum.video;
  }

  public get isAudio(): boolean {
    return this.item.type === ContentTypeEnum.audio;
  }

  public get isImage(): boolean {
    return this.item.type === ContentTypeEnum.image;
  }

  public get isMediaTypeNecessary() {
    return this.isVideo || this.isAudio || this.isImage;
  }

  public get status(): string {
    if (this._item.archived_at) {
      // этот вариант недопустим на самом деле
      // ;( throw new Error('Archived content in <app-content-selector> not allowed');
      return 'archived';
    }

    if (this._item.valid === false) {
      return 'draft';
    }

    if (
      this._item.moderation_status === ModerationStatusEnum.softDeclined ||
      this._item.moderation_status === ModerationStatusEnum.hardDeclined
    ) {
      return 'declined';
    }

    return null;
  }

  public get statusTitle(): string {
    switch (this.status) {
      case 'archived':
        return 'content-mini-card.archived';

      case 'draft':
        return 'content-mini-card.draft';

      case 'declined':
        return 'content-mini-card.declined';
    }
  }

  private loadAuthorDetails(id: string) {
    if (!id) {
      return;
    }
    this.accountService
      .getUserById(id, 'LOAD_TIME')
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (user) => {
          this.author = user;
          this.authorName = AccountService.getUserName(this.author);
          this.authorFullName = AccountService.getUserFullName(this.author);
          this.cover = {
            img: FilesService.getFileUrlBySize(this.author.userpic_id, ImageSizeEnum.sm),
            title: this.authorFullName,
          };
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  protected prepareContentCard() {
    this.loadAuthorDetails(this._item.author_id);
    this.fullTitle = this.contentService.contentFullTitle(this._item);
    this.subject = (this._item.subject ? this._item.subject.title : null) || '---';
    if (!!this._item.level_other_name) {
      this.level = this._item.level_other_name;
    } else {
      this.level =
        'level' in this._item && this._item.level
          ? this._item.level.title
          : this.translate.instant('subject-levels.any-level');
    }
    this.contentType = ContentRouter.getNameByContentType(this._item.type);
    this.contentTypeTitle = ContentService.contentTypeTitle[this._item.type];

    this.prepareCoverImage();
    this.detectChanges();
  }

  protected prepareCoverImage() {
    if (!this._item.cover_id && this.isVideo) {
      const _video = this._item as VideoContent;
      const _type = videoType(_video.media_url);
      switch (_type) {
        case 'youtube':
          this.coverImageUrl = youtubeCoverImageUrl(_video.media_url);
          break;

        case 'vimeo':
          this.coverImageUrl = vimeoCoverImageUrl(_video.media_url);
          break;
      }
      return;
    }
    this.coverImageUrl = FilesService.getFileUrlBySize(this._item?.cover_id);
  }
}
